// Shared text-input validation and submit state for shadcn Input call sites.
//
// Submit pipeline: raw -> trim -> transforms? -> empty -> rules -> moderation? -> onSubmit
// - rules: names from shared/validation/inputRules (single source of truth, FE+BE).
// - checkModCensors: opt-in profanity check; reads room mod settings via useRoomConfig.
// - onSubmit returning `false` preserves the value; default clears it on success.

import React from 'react';
import {
  getCensorMatcher,
  resolveCensorshipRule,
} from '@airhead-games/shared/validation/censorship';
import { validateInput } from '@airhead-games/shared/validation/inputRules';
import { applyInputTransforms } from '@airhead-games/shared/validation/inputTransforms';
import { createLogger } from '@airhead-games/shared/utilities';
import { useRoomConfig } from './useRoomConfig';

const logger = createLogger('useValidatedInput');

export class InputValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'InputValidationError';
  }
}

interface UseValidatedInputArgs {
  initialValue?: string;
  rules?: string[];
  checkModCensors?: boolean;
  transforms?: string[];
  emptyMessage?: string;
  // Return `false` to preserve the input value after a successful submit
  // (e.g. when the form stays on screen). Default behavior clears it.
  onSubmit: (transformed: string) => Promise<void | false> | void | false;
}

interface UseValidatedInputResult {
  value: string;
  setValue: (v: string) => void;
  error: string | null;
  setError: (e: string | null) => void;
  isSubmitting: boolean;
  inputProps: {
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    'aria-invalid': boolean;
    disabled: boolean;
  };
  submit: (valueOverride?: string) => Promise<void>;
}

export function useValidatedInput({
  initialValue = '',
  rules = [],
  checkModCensors = false,
  transforms = [],
  emptyMessage = 'Input cannot be empty',
  onSubmit,
}: UseValidatedInputArgs): UseValidatedInputResult {
  const [value, setValue] = React.useState(initialValue);
  const [error, setError] = React.useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  // Safe to call from non-room flows (auth, Join). They keep checkModCensors=false, so
  // the values are read but never branched on.
  const {
    censorCommon,
    censorCustom,
    censorRigid,
    censorCustomWords,
    twitchTermsBlocked,
    censorTwitchWords,
  } = useRoomConfig();

  React.useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const submit = React.useCallback(
    async (valueOverride?: string) => {
      if (isSubmitting) return;

      // Trim is baked in (universal hygiene). named transforms handle caller-specific
      // normalization like lowercase/capitalize-first, applied AFTER trim.
      const sourceValue = valueOverride ?? value;
      const trimmed = sourceValue.trim();
      const normalized =
        transforms.length > 0
          ? applyInputTransforms(transforms, trimmed)
          : trimmed;

      if (!normalized) {
        setError(emptyMessage);
        return;
      }

      for (const rule of rules) {
        const ruleError = validateInput(rule, normalized);
        if (ruleError) {
          setError(ruleError);
          return;
        }
      }

      setError(null);
      setIsSubmitting(true);

      // Moderation runs inside the guarded path so a matcher failure surfaces a refusal
      // rather than an unhandled rejection that leaves the field silently dead.
      try {
        if (checkModCensors) {
          const matcher = await getCensorMatcher(
            resolveCensorshipRule({
              censorCommon,
              censorCustom,
              censorRigid,
              censorCustomWords,
              twitchTermsBlocked,
              censorTwitchWords,
            })
          );
          if (matcher(normalized)) {
            setError('Please enter something else');
            return;
          }
        }

        const result = await onSubmit(normalized);
        if (result !== false) {
          setValue('');
        }
      } catch (err) {
        if (err instanceof InputValidationError) {
          setError(err.message);
          return;
        }
        logger.error('Validated input submit failed', err);
        setError('An error occurred. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      checkModCensors,
      censorCommon,
      censorCustom,
      censorRigid,
      censorCustomWords,
      twitchTermsBlocked,
      censorTwitchWords,
      emptyMessage,
      isSubmitting,
      onSubmit,
      rules,
      transforms,
      value,
    ]
  );

  return {
    value,
    setValue,
    error,
    setError,
    isSubmitting,
    inputProps: {
      value,
      onChange: (event) => {
        setValue(event.target.value);
        if (error) setError(null);
      },
      onKeyDown: (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          void submit();
        }
      },
      'aria-invalid': !!error,
      disabled: isSubmitting,
    },
    submit,
  };
}
